import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { regionConfig, currentRegion } from "../config/regions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Shield, Loader2, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import BrandLockup from "../components/BrandLockup";
import { useClinicalShellTheme } from "../hooks/useBrandShellTheme";
import { clearPkceVerifiers } from "../lib/pkceStorage";

const ResetPasswordPage = () => {
  useClinicalShellTheme();
  const { t, language } = useLanguage();
  const complianceLabel =
    regionConfig.compliance_label?.[language] ||
    regionConfig.compliance_label?.en ||
    "";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isUsRegion = currentRegion === "us";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [sessionChecking, setSessionChecking] = useState(isUsRegion);
  const [recoveryReady, setRecoveryReady] = useState(!isUsRegion && Boolean(token));

  useEffect(() => {
    if (!isUsRegion) return;

    let cancelled = false;

    const initSupabaseRecovery = async () => {
      const { supabase } = await import("../lib/supabase");
      if (cancelled) return;
      if (!supabase) {
        setError(t("reset.failed"));
        setSessionChecking(false);
        return;
      }

      try {
        const code = searchParams.get("code");
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (cancelled) return;
          if (!exchangeError) {
            clearPkceVerifiers(window.localStorage);
          }
          if (exchangeError) {
            setError(exchangeError.message);
            setSessionChecking(false);
            return;
          }
        }

        const { data, error: sessionError } = await supabase.auth.getSession();
        if (cancelled) return;
        if (sessionError) {
          setError(sessionError.message);
        } else if (data?.session?.access_token) {
          setRecoveryReady(true);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || t("reset.failed"));
      } finally {
        if (!cancelled) {
          setSessionChecking(false);
        }
      }
    };

    initSupabaseRecovery();

    return () => {
      cancelled = true;
    };
  }, [isUsRegion, searchParams, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error(t("reset.passwordMin"));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("reset.passwordMismatch"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isUsRegion) {
        const { supabase } = await import("../lib/supabase");
        if (!supabase) {
          throw new Error(t("reset.failed"));
        }
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) {
          throw updateError;
        }
      } else {
        await axios.post(`${API}/auth/reset-password/confirm`, {
          token,
          new_password: password,
        });
      }
      setSuccess(true);
      toast.success(t("reset.success"));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError(detail || t("reset.linkInvalid"));
      toast.error(detail || t("reset.failed"));
    } finally {
      setLoading(false);
    }
  };

  if (sessionChecking) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="reset-password-page">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!recoveryReady) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="reset-password-page">
        <div className="w-full max-w-sm bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <XCircle className="w-12 h-12 text-destructive mx-auto" />
          <h2 className="font-display text-xl">
            {error ? t("reset.failed") : t("reset.invalidLink")}
          </h2>
          <p
            className={`text-sm ${error ? "text-destructive" : "text-muted-foreground"}`}
            data-testid="reset-init-error-msg"
          >
            {error || t("reset.tokenMissing")}
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full font-semibold"
            data-testid="back-to-login-btn"
          >
            {t("reset.backToLogin")}
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="reset-password-page">
        <div className="w-full max-w-sm bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-consensus mx-auto" />
          <h2 className="font-display text-xl">{t("reset.passwordUpdated")}</h2>
          <p className="text-sm text-muted-foreground">{t("reset.canSignIn")}</p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full font-semibold"
            data-testid="go-to-login-btn"
          >
            {t("reset.signIn")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm space-y-8 flex flex-col items-center">
        <h1 className="font-display text-4xl tracking-tight font-bold">
          <BrandLockup
            as="span"
            markSize={40}
            className="font-display text-4xl tracking-tight font-bold justify-center"
          />
        </h1>

        <div className="w-full bg-card border border-border rounded-lg p-8 space-y-5">
          <div className="text-center space-y-2">
            <h2 className="font-display text-xl">{t("reset.newPassword")}</h2>
            <p className="text-sm text-muted-foreground">{t("reset.enterNewPassword")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div className="relative">
              <Input
                data-testid="new-password-input"
                type={showPassword ? "text" : "password"}
                placeholder={t("reset.newPassword")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-11 bg-background pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                data-testid="toggle-password-btn"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <Input
              data-testid="confirm-password-input"
              type={showPassword ? "text" : "password"}
              placeholder={t("reset.confirmPassword")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="h-11 bg-background"
              autoComplete="new-password"
            />

            {error && (
              <p className="text-sm text-destructive text-center" data-testid="reset-error-msg">
                {error}
              </p>
            )}

            <Button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 font-semibold"
            >
              {loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
              {t("reset.savePassword")}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => navigate("/login")}
              data-testid="back-to-login-link"
            >
              {t("reset.backToLogin")}
            </button>
          </p>
        </div>

        {complianceLabel ? (
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground uppercase tracking-wider">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3 h-3 text-primary" />
              <span>{complianceLabel}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
